import React from 'react';
import { isEqual } from 'lodash';
import history from '@history';
import { useLocation } from 'react-router-dom';
import { useDeepCompareEffect } from '@fuse/hooks';

import { getDefaultFilters } from 'app/main/configs';
import { type MiddlewhereFilter, type MiddlewhereFilterValue } from '../filter/FilterItemManager';
import useSettings from './useSettings';
import useUserData from './useUserData';

export interface FiltersType {
	defaultFilters: MiddlewhereFilter[];
	filters: MiddlewhereFilter[];
	setFilters: React.Dispatch<React.SetStateAction<MiddlewhereFilter[]>>;
	currentFilters: MiddlewhereFilter[];
	setCurrentFilters: React.Dispatch<React.SetStateAction<MiddlewhereFilter[]>>;
	handleFilterChange: (name: string, clause: string, value: MiddlewhereFilterValue) => void;
	handleFiltersApply: () => MiddlewhereFilter[];
	handleFiltersClear: VoidFunction;
}

export interface FiltersProps {
	id?: string;
	module: string;
	defaultFilters?: MiddlewhereFilter[];
	filters?: MiddlewhereFilter[];
}

export default function useFilters(props: FiltersProps) {
	const { settings } = useSettings(props.module);
	const userData = useUserData();
	const location = useLocation();

	const defaultFilters = getDefaultFilters({ settings, defaultFilters: props.defaultFilters || [], userData });

	const locationFilters = location.state && props.id ? location.state[props.id] || [] : null;

	function setLocationStateFilters(newFilters) {
		if (props.id) history.push(location.pathname, { ...location.state, [props.id]: newFilters });
	}

	const baseFilters = locationFilters ?? (props.filters || []);

	const [filters, setFilters] = React.useState<MiddlewhereFilter[]>([...baseFilters, ...defaultFilters]);
	const [currentFilters, setCurrentFilters] = React.useState<MiddlewhereFilter[]>(baseFilters);

	useDeepCompareEffect(() => {
		setFilters([...(props.filters || []), ...defaultFilters]);
		setCurrentFilters(props.filters || []);
	}, [defaultFilters, props.filters]);

	function handleFilterChange(name: string, clause: string, value: MiddlewhereFilterValue) {
		setCurrentFilters(oldFilters => {
			// Removes previous value
			const oldFilter = oldFilters.find(item => item.filt_field === name);
			if (oldFilter) oldFilters.splice(oldFilters.indexOf(oldFilter), 1);

			// If value isn't null adds it to the filters
			if (value !== null) {
				if (value !== '' && !isEqual(value, [])) {
					if (typeof value === 'string') value = value.replace(/'/g, '’');

					oldFilters.push({
						filt_field: name,
						filt_clause: clause,
						filt_value: value
					});
				}
			} else if (clause === 'is_null' || clause === 'is_not_null') {
				oldFilters.push({
					filt_field: name,
					filt_clause: clause
				});
			} else {
				oldFilters.push({
					filt_field: name,
					filt_clause: clause,
					filt_value: null
				});
			}

			return oldFilters;
		});
	}

	function handleFiltersApply() {
		const cleanedCurrentFilters = currentFilters.filter(filt => filt.filt_value !== null);
		setFilters([...cleanedCurrentFilters, ...defaultFilters]);
		setLocationStateFilters(cleanedCurrentFilters);
		return cleanedCurrentFilters;
	}

	function handleFiltersClear() {
		setFilters([...defaultFilters]);
		setLocationStateFilters([]);
		setCurrentFilters([]);
	}

	const filterResult: FiltersType = {
		defaultFilters: defaultFilters || [],
		filters,
		setFilters,
		currentFilters,
		setCurrentFilters,
		handleFilterChange,
		handleFiltersApply,
		handleFiltersClear
	};

	return filterResult;
}
